import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Toast } from './Toast';
import { Tooltip } from './Tooltip';
import { Button } from './Button';
import { Spinner } from './Spinner';
import { FeedbackModal } from './FeedbackModal';
import { BadRatingDiagnosisCard } from './BadRatingDiagnosisCard';

export function RatingButtons({ messageId, sessionId, existingRatings, onRate, onAnalyze, onMarkReview }) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [analyzingId, setAnalyzingId] = useState(null);
  const [localAnalyses, setLocalAnalyses] = useState({});

  // Check if message has already been rated
  const hasRating = existingRatings && existingRatings.length > 0;

  // Bound "mark for review" callback shared by the modal and the diagnosis card.
  // Returns the updated rating (with nested analysis) so callers can refresh in place.
  const markReview = onMarkReview
    ? (notConform, comment) => onMarkReview({ messageId, sessionId, notConform, comment })
    : undefined;

  const handleThumbsUp = async () => {
    setIsLoading(true);
    try {
      await onRate({
        messageId,
        sessionId,
        rating: 'good',
        feedback: '',
      });
      // No toast on success
    } catch (error) {
      setToastMessage(error?.error || error?.message || 'Error rating message');
    } finally {
      setIsLoading(false);
    }
  };

  const handleThumbsDown = () => {
    setShowFeedbackModal(true);
  };

  // Returns the created rating (with nested `analysis`) so FeedbackModal can render
  // the diagnosis. Errors propagate to the modal (it keeps the form open + shows them).
  const handleFeedbackSubmit = (feedback) =>
    onRate({
      messageId,
      sessionId,
      rating: 'bad',
      feedback,
    });

  // On-demand analysis for an existing bad rating that was never diagnosed.
  const handleAnalyze = async (ratingId) => {
    setAnalyzingId(ratingId);
    try {
      const result = await onAnalyze({ messageId, sessionId });
      if (result?.analysis) {
        setLocalAnalyses((prev) => ({ ...prev, [ratingId]: result.analysis }));
      }
    } catch (error) {
      setToastMessage(error?.error || error?.message || 'Error analyzing message');
    } finally {
      setAnalyzingId(null);
    }
  };

  return (
    <>
      <div className="space-y-3">
        {/* Rating Buttons - only show if no existing rating */}
        {!hasRating && (
          <div className="flex">
            {/* Thumbs up with tooltip */}
            <Tooltip content={t('conversations.rateGood')} placement="bottom">
              <button
                onClick={handleThumbsUp}
                disabled={isLoading}
                className="cursor-pointer p-1 rounded group"
              >
                <Icon icon="lucide:thumbs-up" className="size-4 text-muted group-hover:opacity-60 transition-opacity" />
              </button>
            </Tooltip>

            {/* Thumbs down with tooltip */}
            <Tooltip content={t('conversations.rateBad')} placement="bottom">
              <button
                onClick={handleThumbsDown}
                disabled={isLoading}
                className="cursor-pointer p-1 rounded group"
              >
                <Icon icon="lucide:thumbs-down" className="size-4 text-muted group-hover:opacity-60 transition-opacity" />
              </button>
            </Tooltip>
          </div>
        )}

        {/* Existing Ratings */}
        {existingRatings && existingRatings.length > 0 && (
          <div className="text-xs text-muted space-y-1">
            <div className="font-medium">{t('conversations.ratedBy')}:</div>
            {existingRatings.map((rating) => (
              <div key={rating.id} className="space-y-2">
                <div className="flex items-center gap-2">
                  <Icon
                    icon={rating.rating === 'good' ? 'lucide:thumbs-up' : 'lucide:thumbs-down'}
                    className={`size-3 ${rating.rating === 'good' ? 'text-success' : 'text-error'}`}
                  />
                  <span>
                    {rating.rated_by.first_name} {rating.rated_by.last_name}
                    {rating.feedback && `: "${rating.feedback}"`}
                  </span>
                </div>
                {(() => {
                  const analysis = rating.analysis || localAnalyses[rating.id];
                  if (analysis) {
                    return <BadRatingDiagnosisCard analysis={analysis} onMarkReview={markReview} />;
                  }
                  // Bad rating without a diagnosis yet: offer on-demand analysis
                  if (rating.rating === 'bad' && onAnalyze) {
                    return analyzingId === rating.id ? (
                      <div className="flex items-center gap-2 text-xs text-muted">
                        <Spinner size="sm" />
                        {t('conversations.diagnosis.analyzingBody', 'Analyzing why this answer was rated bad…')}
                      </div>
                    ) : (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleAnalyze(rating.id)}
                        className="cursor-pointer"
                      >
                        <Icon icon="lucide:sparkles" className="size-3.5" />
                        {t('conversations.diagnosis.analyzeCta', 'Diagnose reason')}
                      </Button>
                    );
                  }
                  return null;
                })()}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        onSubmit={handleFeedbackSubmit}
        onMarkReview={markReview}
      />

      {/* Toast Notification */}
      {toastMessage && (
        <Toast
          message={toastMessage}
          onClose={() => setToastMessage('')}
        />
      )}
    </>
  );
}

RatingButtons.propTypes = {
  messageId: PropTypes.string.isRequired,
  sessionId: PropTypes.string.isRequired,
  onRate: PropTypes.func.isRequired,
  onAnalyze: PropTypes.func,
  onMarkReview: PropTypes.func,
  existingRatings: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      rating: PropTypes.string.isRequired,
      feedback: PropTypes.string,
      analysis: PropTypes.object,
      rated_by: PropTypes.shape({
        first_name: PropTypes.string,
        last_name: PropTypes.string,
      }).isRequired,
    })
  ),
};
